import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Radio, Shield, AlertTriangle, Ban, Heart, MessageCircle, 
  DollarSign, Users, CheckCircle, XCircle 
} from 'lucide-react';

export default function LiveRulesPage() {
  return (
    <div className="min-h-screen bg-background py-12" data-testid="live-rules-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Radio className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="font-heading text-4xl font-bold mb-4">Live Streaming Rules</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            To keep DMV Soundstage a safe and positive community, all live streamers and viewers 
            must follow these guidelines.
          </p>
        </div>

        {/* Content Rules */}
        <Card className="glass-card border-border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Content Guidelines
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Allowed Content</p>
                <p className="text-sm text-muted-foreground">
                  Music performances, DJ sets, podcast recordings, behind-the-scenes, Q&As, 
                  creative sessions, and community hangouts.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Prohibited Content</p>
                <p className="text-sm text-muted-foreground">
                  Nudity, sexual content, violence, illegal activities, drug use, hate speech, 
                  harassment, copyrighted material without rights, and spam.
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Chat Rules */}
        <Card className="glass-card border-border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-secondary" />
            Chat Conduct
          </h2>
          
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              <span>Be respectful to the host and other viewers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              <span>No hate speech, slurs, or discriminatory language</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              <span>No harassment, threats, or personal attacks</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              <span>No spam, excessive caps, or disruptive behavior</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-secondary">•</span>
              <span>No solicitation or advertising without permission</span>
            </li>
          </ul>
        </Card>

        {/* Strike System */}
        <Card className="glass-card border-amber-500/30 p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Three-Strike Policy
          </h2>
          
          <p className="text-sm text-muted-foreground mb-4">
            Violations of chat rules result in strikes on your account:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <Badge className="bg-amber-500 mb-2">Strike 1</Badge>
              <p className="text-sm">Warning issued. Message filtered or removed.</p>
            </div>
            <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <Badge className="bg-orange-500 mb-2">Strike 2</Badge>
              <p className="text-sm">Final warning. Continued violations will result in ban.</p>
            </div>
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <Badge className="bg-red-500 mb-2">Strike 3</Badge>
              <p className="text-sm">Temporary chat ban. Unable to send messages.</p>
            </div>
          </div>
        </Card>

        {/* Monetization Rules */}
        <Card className="glass-card border-border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Gifts & Tips
          </h2>
          
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              <span>All coin purchases are final and non-refundable</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              <span>Creators receive 70% of gift value (30% platform fee)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              <span>Tips have a 10% platform fee</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              <span>Earnings have a 7-day hold before payout eligibility</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">•</span>
              <span>Minimum payout threshold is $25</span>
            </li>
          </ul>
        </Card>

        {/* Host Requirements */}
        <Card className="glass-card border-border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Host Requirements
          </h2>
          
          <p className="text-sm text-muted-foreground mb-4">
            To go live on DMV Soundstage, you must:
          </p>
          
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
              <span>Have an active artist profile</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
              <span>Maintain an active subscription</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
              <span>Complete Stripe Connect payout setup</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
              <span>Have fewer than 3 strikes on your account</span>
            </li>
          </ul>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            By using DMV Soundstage live features, you agree to these rules and our{' '}
            <a href="/terms" className="text-primary hover:underline">Terms of Service</a> and{' '}
            <a href="/guidelines" className="text-primary hover:underline">Community Guidelines</a>.
          </p>
          <p className="mt-2">Last updated: January 2025</p>
        </div>
      </div>
    </div>
  );
}
